import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { change, getFormValues } from 'redux-form';

import PackageGallery from '../PackageGallery';
import Select from '../Select';
import { getNumberOfRoomsOptions } from '../../utils';
import { getPackageTotalPrice, isWithoutAccommodation } from '../../apiHelpers';
import { WITHOUT_ACCOMMODATION_ROOM_ID } from '../../constants';
import withLocalize from '../../withLocalize';
import T from '../Translate';
import noRoomsIcon from '../../images/no-rooms.svg';

const MAX_ROOMS = 3;

// Pouze pro hotel sandamiano - nahrazuje Rooms/index.jsx, viz Step1.jsx
class RoomsSandamiano extends Component {

	/** Vrací celkový počet dostupných (neobsazených) pokojů (resp. lůžek). */
	getTotalVacant = () => {
		return this.props.roomOffers.reduce((sum, roomOffer) => sum + roomOffer.noOfVacant, 0);
	};

	/** Vrací počet vybraných pokojů (resp. lůžek). */
	getSelectedCount = () => {
		const rooms = (this.props.formValues && this.props.formValues.rooms) || {};
		return Object.values(rooms).reduce((sum, roomsOfType) => sum + roomsOfType.length, 0);
	};

	/** Vrací hodnotu selectu - "bez ubytování", počet vybraných pokojů (resp. lůžek), nebo prázdnou hodnotu. */
	getSelectedValue = () => {
		if (isWithoutAccommodation(this.props.formValues)) {
			return WITHOUT_ACCOMMODATION_ROOM_ID;
		}

		return this.getSelectedCount() || '';
	};

	/** Vrací options selectu vč. případné položky "Bez ubytování" na prvním místě. */
	getOptions = () => {
		const { eventPackage, translateSelectOptions } = this.props;
		const options = [];

		if (eventPackage.allowWithoutAccommodation) {
			options.push({ value: WITHOUT_ACCOMMODATION_ROOM_ID, label: [['utils.getNumberOfRoomsOptions.withoutAccommodation']] });
		}

		if (this.getTotalVacant() > 0) {
			options.push(...getNumberOfRoomsOptions(Math.min(MAX_ROOMS, this.getTotalVacant())));
		}

		return translateSelectOptions(options);
	};

	/** Rezervace pouze kalendářové události - místo pokojů vybereme jeden pseudo-pokoj s kapacitou dle události. */
	handleChangeWithoutAccommodation = () => {
		const { guestTypes, defaultGuests, eventPackage, dispatch } = this.props;

		const capacity = eventPackage.eventMaxPpr || 1;
		const guestTypesItems = {};
		Object.values(guestTypes).forEach(guestType => {
			guestTypesItems[guestType.id] = guestType.default ? Math.min(defaultGuests, capacity) : 0;
		});

		dispatch(change('bookingForm', 'rooms', {
			[WITHOUT_ACCOMMODATION_ROOM_ID]: [{
				id: WITHOUT_ACCOMMODATION_ROOM_ID,
				beds: capacity,
				extrabeds: 0,
				guestTypes: guestTypesItems,
				package: eventPackage.id,
				withoutAccommodation: true,
			}],
		}));
	};

	handleChange = (value) => {
		const { roomOffers, guestTypes, defaultMaxPpr, defaultGuests, eventPackage, dispatch } = this.props;

		if (value === WITHOUT_ACCOMMODATION_ROOM_ID) {
			this.handleChangeWithoutAccommodation();
			return;
		}

		let remaining = value ? parseInt(value, 10) : 0;
		const rooms = {};

		for (const roomOffer of roomOffers) {
			if (remaining <= 0) break;

			const take = Math.min(remaining, roomOffer.noOfVacant);
			if (take <= 0) continue;

			rooms[roomOffer.id] = [];
			for (let i = 0; i < take; i++) {
				const guestTypesItems = {};
				Object.values(guestTypes).forEach(guestType => {
					guestTypesItems[guestType.id] = guestType.default
						? (defaultMaxPpr ? roomOffer.beds + roomOffer.extrabeds : Math.min(defaultGuests, roomOffer.beds + roomOffer.extrabeds))
						: 0;
				});

				rooms[roomOffer.id].push({
					...roomOffer,
					guestTypes: guestTypesItems,
					package: eventPackage.id,
				});
			}

			remaining -= take;
		}

		dispatch(change('bookingForm', 'rooms', rooms));
	};

	render() {
		const { eventPackage, noOfNights, formValues, initiated, isLoading, apiRoomsResponse, translate: t, translatePropertyValue } = this.props;

		if (!formValues) return null;

		const { currency } = formValues;

		// pokud lze balíček rezervovat i bez ubytování, nabídku zobrazíme i v případě, že nejsou volné pokoje
		const noRoomReason = !isLoading && this.getTotalVacant() === 0 && !eventPackage.allowWithoutAccommodation
			? (['closedCheckIn', 'closedCheckOut', 'minNights', 'maxNights'].find(field => apiRoomsResponse[field]) || 'empty')
			: undefined;

		return (
			<div className="rooms">
				<div className="room-offers">
					<fieldset className="fieldset">
						<legend><T id="Rooms.index.title" /></legend>
					</fieldset>

					{!initiated &&
					<div className="fullscreen-loader">
						<div className="loader loader--large"/>
					</div>
					}

					{initiated && !noRoomReason &&
					<div className="room-offer room-offer--last">
						{eventPackage.photos && eventPackage.photos.length > 0 &&
						<PackageGallery
							id={eventPackage.id}
							photos={eventPackage.photos}
							alt={translatePropertyValue(eventPackage.name)}
						/>
						}
						<div className="room-offer__text-container">
							<h2 className="room-offer__room-name">{translatePropertyValue(eventPackage.name)}</h2>
							{translatePropertyValue(eventPackage.description) &&
							<p className="room-offer__description">{translatePropertyValue(eventPackage.description)}</p>
							}
							<div className="room-offer-count">
								<div className="room-offer-count__price-container">
									<div className="room-offer-count__price">
										{getPackageTotalPrice(eventPackage, noOfNights, currency)}
										{eventPackage.daily &&
										<span className="room-offer-count__persons"> / {t('Step3.packages.perStay')}</span>
										}
										{eventPackage.perGuest &&
										<span className="room-offer-count__persons"> / {t('Step3.packages.perGuest')}</span>
										}
									</div>
								</div>
								<Select
									className="room-offer-count__select"
									label={t('Rooms.RoomOffer.selectNoOfRooms')}
									clearable={true}
									options={this.getOptions()}
									input={{
										value: this.getSelectedValue(),
										onChange: this.handleChange,
									}}
								/>
							</div>
						</div>
					</div>
					}

					{initiated && noRoomReason &&
						<div className="text-center">
							<img src={noRoomsIcon} className="room-offers-empty__icon" alt="" />
							<h2 className="room-offers-empty__title"><T id={`Rooms.index.${noRoomReason}.title`} /></h2>
							<h3 className="room-offers-empty__subtitle"><T id={`Rooms.index.${noRoomReason}.subtitle`} /></h3>
						</div>
					}
				</div>
			</div>
		);
	}
}

export default compose(
	connect((state) => ({
		formValues: getFormValues('bookingForm')(state),
		initiated: state.app.initiated,
		isLoading: state.app.isLoading,
		apiRoomsResponse: state.app.apiRoomsResponse,
		roomOffers: state.app.roomOffers,
		noOfNights: state.app.noOfNights,
		guestTypes: state.app.guestTypes,
		defaultMaxPpr: state.app.defaultMaxPpr,
		defaultGuests: state.app.guests,
		eventPackage: state.app.package,
	})),
	withLocalize,
)(RoomsSandamiano);



// WEBPACK FOOTER //
// ./src/components/Rooms/RoomsSandamiano.jsx